import { CHART_TYPE_AREA, CHART_TYPE_BARS } from "../../constants";
import { LINE_SERIES_CAP_RATIO } from "../constants";
import { candleShapes } from "./candles";
import { barShapes } from "./ohlcBars";

/**
 * Draws the main series through the WebGL renderers. Pan and zoom change
 * only the x / y transforms and the bar shapes, never the buffers.
 * Line: the gradient band from the line down to the pane bottom (the
 * gradient spans the whole pane height, 04-line-area.jpg), then the line.
 *
 * @param {object} params
 * @param {number[]} params.viewport - [width, height] of the canvas, device px
 * @param {object} params.timeScale
 * @param {object} params.priceScale
 * @param {number} params.pixelRatio
 * @param {number} params.paneHeight - CSS px
 * @param {{ rects: object, band: object, line: object }} params.renderers
 * @param {number} params.type - CHART_TYPE_* value
 * @param {number} params.priceBase - price the instances are relative to
 * @param {string} params.shadowStyle - SHADOW_STYLE value
 * @param {Float32Array} params.palette - up / down colours of the rects
 * @param {Object<string, { rgba: number[] }>} params.colors - theme colours
 * @param {boolean} params.isSnappedX - see rectRenderer.draw
 */
export const drawSeries = ({
  viewport,
  timeScale,
  priceScale,
  pixelRatio,
  paneHeight,
  renderers,
  type,
  priceBase,
  shadowStyle,
  palette,
  colors,
  isSnappedX,
}) => {
  const { scale, offset } = priceScale.relativeTransform(priceBase);
  const xTransform = [timeScale.barSpacing * pixelRatio, timeScale.offset * pixelRatio];
  const yTransform = [scale * pixelRatio, offset * pixelRatio];

  if (type === CHART_TYPE_AREA) {
    const paneBottom = paneHeight * pixelRatio;

    renderers.band.draw({
      viewport,
      xTransform,
      yTransform,
      bottom: paneBottom,
      gradientRows: [0, paneBottom],
      topColor: colors.areaTop.rgba,
      bottomColor: colors.areaBottom.rgba,
    });
    renderers.line.draw({
      viewport,
      xTransform,
      yTransform,
      widthScale: pixelRatio,
      capRatio: LINE_SERIES_CAP_RATIO,
    });
    return;
  }

  const shapes =
    type === CHART_TYPE_BARS
      ? barShapes(timeScale.barSpacing, pixelRatio)
      : candleShapes(timeScale.barSpacing, pixelRatio, shadowStyle);

  renderers.rects.draw({ viewport, xTransform, yTransform, shapes, palette, isSnappedX });
};
